import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  FileText, 
  Download, 
  Code, 
  Database, 
  Server, 
  Layers,
  TrendingUp,
  Clock,
  DollarSign,
  Users,
  CheckCircle2,
  Loader2,
  Wrench
} from 'lucide-react';
import html2pdf from 'html2pdf.js';

const TechnicalReport = () => {
  const [generating, setGenerating] = useState(false);
  
  // Fecha actual para el informe
  const reportDate = new Date();
  const formattedDate = reportDate.toLocaleDateString('es-ES', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });
  const monthYear = reportDate.toLocaleDateString('es-ES', { 
    month: 'long', 
    year: 'numeric' 
  });

  const metrics = {
    linesOfCode: { frontend: 33454, backend: 16497, total: 49951 },
    files: { frontend: 127, backend: 75, total: 202 },
    apiEndpoints: 129,
    components: { pages: 20, components: 25, uiComponents: 46, hooks: 5 },
    models: 16,
    routers: 23,
    testIds: 391
  };

  // =========================================================================
  // COMPARATIVA DE TRES ESCENARIOS TECNOLOGICOS
  // =========================================================================

  // ESCENARIO 1: Desarrollo Tradicional (4 meses)
  const scenario1 = {
    name: 'Desarrollo Tradicional',
    subtitle: 'El estandar clasico',
    duration: '4 meses',
    businessTasks: [
      { task: 'Toma de requisitos y definicion del backlog', hours: 80 },
      { task: 'Reuniones de seguimiento y alineacion', hours: 40 },
      { task: 'Validacion de entregables y UAT', hours: 80 },
    ],
    businessHours: 200,
    businessRate: 80,
    businessCost: 16000,
    techTasks: [
      { task: 'Diseno de interfaces (Figma) y prototipado UX/UI', hours: 160 },
      { task: 'Configuracion de arquitectura y base de datos', hours: 120 },
      { task: 'Desarrollo de 129 Endpoints API y logica (Backend)', hours: 360 },
      { task: 'Desarrollo de 45 Componentes React y vistas (Frontend)', hours: 360 },
      { task: 'Integracion de sistemas y APIs de IA', hours: 120 },
      { task: 'Pruebas de calidad (QA), correccion de bugs y despliegue', hours: 280 },
    ],
    techHours: 1400,
    techRate: 50,
    techCost: 70000,
    totalHours: 1600,
    totalCost: 86000
  };

  // ESCENARIO 2: Tradicional + Asistentes IA tipo Copilot (2.5 meses)
  const scenario2 = {
    name: 'Tradicional + Asistentes IA',
    subtitle: 'El estandar ACTUAL de TYPSA',
    duration: '2,5 meses',
    note: 'La IA acelera la escritura de codigo, pero persisten las reuniones, el diseno manual y los traspasos entre departamentos.',
    businessTasks: [
      { task: 'Toma de requisitos y definicion', hours: 60 },
      { task: 'Reuniones de seguimiento', hours: 30 },
      { task: 'Validacion y UAT', hours: 60 },
    ],
    businessHours: 150,
    businessRate: 80,
    businessCost: 12000,
    techTasks: [
      { task: 'Diseno de interfaces y prototipado UX/UI', hours: 120 },
      { task: 'Configuracion de arquitectura y BBDD', hours: 70 },
      { task: 'Desarrollo de Endpoints API (Backend asistido)', hours: 200 },
      { task: 'Desarrollo de Componentes React (Frontend asistido)', hours: 200 },
      { task: 'Integracion de sistemas y APIs de IA', hours: 90 },
      { task: 'Pruebas de calidad (QA automaticas/asistidas) y despliegue', hours: 150 },
    ],
    techHours: 830,
    techRate: 50,
    techCost: 41500,
    totalHours: 980,
    totalCost: 53500
  };

  // ESCENARIO 3: Vibe Coding con Emergent AI (3 semanas)
  const scenario3 = {
    name: 'Vibe Coding con Emergent AI',
    subtitle: 'El caso de exito',
    duration: '3 semanas',
    note: 'Creacion end-to-end orquestada en lenguaje natural por 1 persona, el Product Owner.',
    tasks: [
      { task: 'Definicion orquestada en lenguaje natural (Vibe Coding)', hours: 16 },
      { task: 'Generacion, conexion e iteracion de componentes con IA', hours: 24 },
      { task: 'Validacion funcional y ajustes finales', hours: 16 },
    ],
    totalHours: 56,
    rateInternal: 142,
    timeCostInternal: 7952,
    emergentCostEUR: 600,
    totalCost: 8552
  };

  // Mantenimiento
  const maintenance = {
    traditional: { annual: 39500, monthly: 3290 },
    withAI: { subscription: 20, credits: 50, poTime: 312, monthly: 382, annual: 4584 }
  };

  // Calculos comparativos
  const savingsVsTraditional = scenario1.totalCost - scenario3.totalCost;
  const savingsVsCopilot = scenario2.totalCost - scenario3.totalCost;
  const hoursReductionVsCopilot = Math.round((1 - scenario3.totalHours / scenario2.totalHours) * 100);
  const hoursReductionVsTraditional = Math.round((1 - scenario3.totalHours / scenario1.totalHours) * 100);
  const costReductionVsTraditional = Math.round((1 - scenario3.totalCost / scenario1.totalCost) * 100);
  const costReductionVsCopilot = Math.round((1 - scenario3.totalCost / scenario2.totalCost) * 100);
  const roi = Math.round((savingsVsTraditional / scenario3.totalCost) * 100);

  const generatePDF = async () => {
    setGenerating(true);
    const element = document.getElementById('report-content');
    const opt = {
      margin: [10, 10, 10, 10],
      filename: 'Informe_Tecnico_CRM_TYPSA_Digital_Solutions.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: 'avoid-all', before: '.page-break' }
    };
    try {
      await html2pdf().set(opt).from(element).save();
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Informe Técnico</h1>
          <p className="text-muted-foreground">CRM TYPSA Digital Solutions - Análisis de Desarrollo</p>
          <p className="text-sm font-medium text-blue-600 mt-1">Fecha del informe: {formattedDate}</p>
        </div>
        <Button onClick={generatePDF} disabled={generating} size="lg">
          {generating ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generando...</>
          ) : (
            <><Download className="w-4 h-4 mr-2" />Descargar PDF</>
          )}
        </Button>
      </div>

      <div id="report-content" className="space-y-6">
        {/* Header */}
        <Card className="bg-gradient-to-r from-blue-600 to-blue-800 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <FileText className="w-10 h-10" />
                <div>
                  <h2 className="text-2xl font-bold">INFORME TÉCNICO</h2>
                  <p className="text-blue-100">CRM TYPSA Digital Solutions - Sistema de Gestión Comercial con IA</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-blue-200">Fecha del informe</p>
                <p className="text-lg font-bold">{formattedDate}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-blue-100 text-sm">Líneas de Código</p>
                <p className="text-2xl font-bold">{metrics.linesOfCode.total.toLocaleString()}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-blue-100 text-sm">Endpoints API</p>
                <p className="text-2xl font-bold">{metrics.apiEndpoints}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-blue-100 text-sm">Componentes</p>
                <p className="text-2xl font-bold">{metrics.components.pages + metrics.components.components}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-blue-100 text-sm">Modelos de Datos</p>
                <p className="text-2xl font-bold">{metrics.models}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Resumen Ejecutivo */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              Resumen Ejecutivo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              El CRM TYPSA Digital Solutions es una plataforma integral de gestión comercial que incorpora 
              capacidades avanzadas de Inteligencia Artificial para la transcripción y análisis automático 
              de notas de voz, gestión de pipeline de oportunidades, y seguimiento de actividades comerciales.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="border rounded-lg p-4">
                <Badge variant="outline" className="mb-2">Complejidad</Badge>
                <p className="font-semibold text-lg">ALTA</p>
                <p className="text-sm text-muted-foreground">Sistema empresarial full-stack con IA</p>
              </div>
              <div className="border rounded-lg p-4">
                <Badge variant="outline" className="mb-2">Stack</Badge>
                <p className="font-semibold text-lg">React + FastAPI + MongoDB</p>
                <p className="text-sm text-muted-foreground">Arquitectura moderna y escalable</p>
              </div>
              <div className="border rounded-lg p-4">
                <Badge variant="outline" className="mb-2">Integraciones IA</Badge>
                <p className="font-semibold text-lg">Whisper + GPT-4o</p>
                <p className="text-sm text-muted-foreground">Transcripción y análisis automático</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Arquitectura */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-blue-500" />
              Arquitectura del Sistema
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2"><Code className="w-4 h-4" /> Frontend</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• React 18.x</li>
                  <li>• TailwindCSS 3.x</li>
                  <li>• Shadcn/UI Components</li>
                  <li>• React Router 6.x</li>
                  <li>• Recharts</li>
                </ul>
                <div className="bg-muted rounded p-2 text-center">
                  <p className="text-2xl font-bold">{metrics.linesOfCode.frontend.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">líneas de código</p>
                </div>
              </div>
              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2"><Server className="w-4 h-4" /> Backend</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Python 3.11+</li>
                  <li>• FastAPI</li>
                  <li>• Pydantic 2.x</li>
                  <li>• Motor (async MongoDB)</li>
                  <li>• Uvicorn</li>
                </ul>
                <div className="bg-muted rounded p-2 text-center">
                  <p className="text-2xl font-bold">{metrics.linesOfCode.backend.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">líneas de código</p>
                </div>
              </div>
              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2"><Database className="w-4 h-4" /> Base de Datos</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• MongoDB (NoSQL)</li>
                  <li>• 14 colecciones</li>
                  <li>• Relaciones entre entidades</li>
                  <li>• Validación Pydantic</li>
                  <li>• Índices optimizados</li>
                </ul>
                <div className="bg-muted rounded p-2 text-center">
                  <p className="text-2xl font-bold">{metrics.models}</p>
                  <p className="text-xs text-muted-foreground">modelos de datos</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Código Generado */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code className="w-5 h-5 text-purple-500" />
              Código Generado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Métrica</th>
                    <th className="text-right p-2">Frontend</th>
                    <th className="text-right p-2">Backend</th>
                    <th className="text-right p-2 font-bold">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-2">Líneas de código</td>
                    <td className="text-right p-2">{metrics.linesOfCode.frontend.toLocaleString()}</td>
                    <td className="text-right p-2">{metrics.linesOfCode.backend.toLocaleString()}</td>
                    <td className="text-right p-2 font-bold">{metrics.linesOfCode.total.toLocaleString()}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-2">Archivos</td>
                    <td className="text-right p-2">{metrics.files.frontend}</td>
                    <td className="text-right p-2">{metrics.files.backend}</td>
                    <td className="text-right p-2 font-bold">{metrics.files.total}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <Separator className="my-4" />
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
              <div><p className="text-2xl font-bold">{metrics.components.pages}</p><p className="text-xs text-muted-foreground">Páginas</p></div>
              <div><p className="text-2xl font-bold">{metrics.components.components}</p><p className="text-xs text-muted-foreground">Componentes</p></div>
              <div><p className="text-2xl font-bold">{metrics.routers}</p><p className="text-xs text-muted-foreground">Routers API</p></div>
              <div><p className="text-2xl font-bold">{metrics.apiEndpoints}</p><p className="text-xs text-muted-foreground">Endpoints</p></div>
              <div><p className="text-2xl font-bold">{metrics.testIds}</p><p className="text-xs text-muted-foreground">Test IDs</p></div>
            </div>
          </CardContent>
        </Card>

        {/* Funcionalidades */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              Funcionalidades Implementadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { module: 'Dashboard Ejecutivo', features: 'KPIs en tiempo real, métricas, tendencias' },
                { module: 'Pipeline de Oportunidades', features: 'Kanban, 6 estados, filtros avanzados' },
                { module: 'Gestión de Empresas', features: 'CRUD, clasificación, historial IA' },
                { module: 'Gestión de Contactos', features: 'CRUD, vinculación, historial relación' },
                { module: 'Actividades', features: 'Calendario, documentación, audio' },
                { module: 'Notas de Voz con IA', features: 'Whisper + GPT-4o, análisis automático' },
                { module: 'Gestión de Tareas', features: 'CRUD, notificaciones, responsables' },
                { module: 'Sistema de Permisos', features: 'Admin, Editor, Viewer' },
              ].map((item, i) => (
                <div key={i} className="border rounded-lg p-3">
                  <p className="font-semibold">{item.module}</p>
                  <p className="text-sm text-muted-foreground">{item.features}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* COMPARATIVA DE TRES ESCENARIOS TECNOLOGICOS */}
        {/* ═══════════════════════════════════════════════════════════════════ */}

        {/* ESCENARIO 1: Desarrollo Tradicional */}
        <Card className="page-break border-red-200">
          <CardHeader className="bg-red-50 dark:bg-red-950/30 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-300">
                  <Users className="w-5 h-5" />
                  Escenario 1: {scenario1.name}
                </CardTitle>
                <CardDescription className="text-red-600/80">{scenario1.subtitle} - {scenario1.duration}</CardDescription>
              </div>
              <Badge variant="outline" className="text-red-600 border-red-300 text-lg px-3 py-1">
                {scenario1.totalHours.toLocaleString()} h
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            {/* Negocio y Definicion */}
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs">NEGOCIO</span>
                Negocio y Definicion ({scenario1.businessHours}h | {scenario1.businessCost.toLocaleString()}€)
              </h4>
              <div className="bg-muted/30 rounded-lg p-3">
                <table className="w-full text-sm">
                  <tbody>
                    {scenario1.businessTasks.map((t, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-1.5">{t.task}</td>
                        <td className="text-right font-medium w-20">{t.hours} h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Diseno y Desarrollo Tecnico */}
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs">TECNICO</span>
                Diseno y Desarrollo Tecnico ({scenario1.techHours.toLocaleString()}h | {scenario1.techCost.toLocaleString()}€)
              </h4>
              <div className="bg-muted/30 rounded-lg p-3">
                <table className="w-full text-sm">
                  <tbody>
                    {scenario1.techTasks.map((t, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-1.5">{t.task}</td>
                        <td className="text-right font-medium w-20">{t.hours} h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-red-100 dark:bg-red-950 rounded-lg p-4 mt-4">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-red-700 dark:text-red-300">TOTAL ESCENARIO 1</span>
                <div className="text-right">
                  <p className="text-2xl font-bold text-red-700 dark:text-red-300">{scenario1.totalCost.toLocaleString()}€</p>
                  <p className="text-sm text-red-600">{scenario1.totalHours.toLocaleString()} horas</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ESCENARIO 2: Tradicional + Copilot */}
        <Card className="border-amber-200">
          <CardHeader className="bg-amber-50 dark:bg-amber-950/30 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                  <Code className="w-5 h-5" />
                  Escenario 2: {scenario2.name}
                </CardTitle>
                <CardDescription className="text-amber-600/80">{scenario2.subtitle} - {scenario2.duration}</CardDescription>
              </div>
              <Badge variant="outline" className="text-amber-600 border-amber-300 text-lg px-3 py-1">
                {scenario2.totalHours.toLocaleString()} h
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <p className="text-sm italic text-muted-foreground border-l-2 border-amber-300 pl-3">
              {scenario2.note}
            </p>

            {/* Negocio y Definicion */}
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs">NEGOCIO</span>
                Negocio y Definicion ({scenario2.businessHours}h | {scenario2.businessCost.toLocaleString()}€)
              </h4>
              <div className="bg-muted/30 rounded-lg p-3">
                <table className="w-full text-sm">
                  <tbody>
                    {scenario2.businessTasks.map((t, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-1.5">{t.task}</td>
                        <td className="text-right font-medium w-20">{t.hours} h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Diseno y Desarrollo Tecnico */}
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs">TECNICO</span>
                Diseno y Desarrollo Tecnico ({scenario2.techHours}h | {scenario2.techCost.toLocaleString()}€)
              </h4>
              <div className="bg-muted/30 rounded-lg p-3">
                <table className="w-full text-sm">
                  <tbody>
                    {scenario2.techTasks.map((t, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-1.5">{t.task}</td>
                        <td className="text-right font-medium w-20">{t.hours} h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-amber-100 dark:bg-amber-950 rounded-lg p-4 mt-4">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-amber-700 dark:text-amber-300">TOTAL ESCENARIO 2</span>
                <div className="text-right">
                  <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{scenario2.totalCost.toLocaleString()}€</p>
                  <p className="text-sm text-amber-600">{scenario2.totalHours.toLocaleString()} horas</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ESCENARIO 3: Vibe Coding con Emergent AI */}
        <Card className="border-green-300 border-2">
          <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-300">
                  <TrendingUp className="w-5 h-5" />
                  Escenario 3: {scenario3.name}
                </CardTitle>
                <CardDescription className="text-green-600/80">{scenario3.subtitle} - {scenario3.duration}</CardDescription>
              </div>
              <Badge className="bg-green-600 text-white text-lg px-3 py-1">
                {scenario3.totalHours} h
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <p className="text-sm italic text-muted-foreground border-l-2 border-green-400 pl-3 bg-green-50 dark:bg-green-950/20 p-3 rounded">
              {scenario3.note}
            </p>

            {/* Desglose de tareas del Product Owner */}
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs">PRODUCT OWNER</span>
                Desglose de tareas ({scenario3.totalHours}h totales)
              </h4>
              <div className="bg-green-50 dark:bg-green-950/20 rounded-lg p-3">
                <table className="w-full text-sm">
                  <tbody>
                    {scenario3.tasks.map((t, i) => (
                      <tr key={i} className="border-b last:border-0 border-green-200">
                        <td className="py-2">{t.task}</td>
                        <td className="text-right font-bold w-20 text-green-700">{t.hours} h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Costes */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="border border-green-200 rounded-lg p-3 bg-white dark:bg-gray-900">
                <p className="text-sm text-muted-foreground">Tiempo PO ({scenario3.totalHours}h x {scenario3.rateInternal}€/h)</p>
                <p className="text-xl font-bold text-green-700">{scenario3.timeCostInternal.toLocaleString()}€</p>
              </div>
              <div className="border border-green-200 rounded-lg p-3 bg-white dark:bg-gray-900">
                <p className="text-sm text-muted-foreground">Plataforma Emergent AI</p>
                <p className="text-xl font-bold text-green-700">{scenario3.emergentCostEUR}€</p>
              </div>
              <div className="border-2 border-green-400 rounded-lg p-3 bg-green-100 dark:bg-green-900">
                <p className="text-sm font-medium text-green-800">TOTAL ESCENARIO 3</p>
                <p className="text-2xl font-bold text-green-700">{scenario3.totalCost.toLocaleString()}€</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* MANTENIMIENTO */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-amber-500" />
              Coste de Mantenimiento Comparativo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border rounded-lg p-4 bg-red-50 dark:bg-red-950">
                <h4 className="font-semibold text-red-700 dark:text-red-300 mb-3">Mantenimiento Tradicional</h4>
                <ul className="text-sm space-y-2 text-muted-foreground">
                  <li>• Corrección de bugs: ~9,000€/año</li>
                  <li>• Actualizaciones seguridad: ~5,500€/año</li>
                  <li>• Funcionalidades menores: ~15,000€/año</li>
                  <li>• Soporte técnico: ~4,000€/año</li>
                  <li>• Infraestructura: ~3,600€/año</li>
                  <li>• Licencias/APIs: ~2,400€/año</li>
                </ul>
                <Separator className="my-3" />
                <div className="flex justify-between">
                  <span className="font-semibold">Total anual</span>
                  <span className="font-bold text-lg">{maintenance.traditional.annual.toLocaleString()}€</span>
                </div>
                <p className="text-sm text-muted-foreground">~{maintenance.traditional.monthly.toLocaleString()}€/mes</p>
              </div>

              <div className="border rounded-lg p-4 bg-green-50 dark:bg-green-950">
                <h4 className="font-semibold text-green-700 dark:text-green-300 mb-3">Mantenimiento con Emergent AI</h4>
                <ul className="text-sm space-y-2 text-muted-foreground">
                  <li>• Suscripción Emergent: <strong>{maintenance.withAI.subscription}€/mes</strong></li>
                  <li>• Créditos adicionales (est.): ~{maintenance.withAI.credits}€/mes</li>
                  <li>• Tiempo PO (~2h/mes × 156,20€): ~{maintenance.withAI.poTime}€/mes</li>
                </ul>
                <Separator className="my-3" />
                <div className="flex justify-between">
                  <span className="font-semibold">Total mensual</span>
                  <span className="font-bold text-lg">~{maintenance.withAI.monthly}€</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold">Total anual</span>
                  <span className="font-bold text-lg">~{maintenance.withAI.annual.toLocaleString()}€</span>
                </div>
              </div>
            </div>

            <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-950 rounded-lg">
              <h4 className="font-semibold mb-2">Ventajas del mantenimiento con IA</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Tiempo resolución bug</p>
                  <p><span className="line-through text-red-500">1-3 días</span> → <span className="text-green-600 font-semibold">10-30 min</span></p>
                </div>
                <div>
                  <p className="text-muted-foreground">Dependencia técnica</p>
                  <p><span className="line-through text-red-500">Alta</span> → <span className="text-green-600 font-semibold">Ninguna</span></p>
                </div>
                <div>
                  <p className="text-muted-foreground">Disponibilidad</p>
                  <p><span className="line-through text-red-500">Horario laboral</span> → <span className="text-green-600 font-semibold">24/7</span></p>
                </div>
                <div>
                  <p className="text-muted-foreground">Coste por incidencia</p>
                  <p><span className="line-through text-red-500">200-500€</span> → <span className="text-green-600 font-semibold">20-50€</span></p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* COMPARATIVA FINAL DE TRES ESCENARIOS */}
        <Card className="border-2 border-green-500 page-break">
          <CardHeader className="bg-gradient-to-r from-gray-50 to-green-50 dark:from-gray-900 dark:to-green-950">
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-500" />
              Comparativa Economica Final - Tres Escenarios
            </CardTitle>
            <CardDescription>El salto de eficiencia del Vibe Coding frente al desarrollo tradicional</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3">Concepto</th>
                    <th className="text-right p-3 text-red-600">Esc. 1: Tradicional</th>
                    <th className="text-right p-3 text-amber-600">Esc. 2: Copilot</th>
                    <th className="text-right p-3 text-green-600 font-bold">Esc. 3: Vibe Coding</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-3">Duracion</td>
                    <td className="text-right p-3">{scenario1.duration}</td>
                    <td className="text-right p-3">{scenario2.duration}</td>
                    <td className="text-right p-3 font-semibold text-green-600">{scenario3.duration}</td>
                  </tr>
                  <tr className="border-b bg-blue-50/50 dark:bg-blue-950/20">
                    <td className="p-3 font-medium">Horas Negocio/PO</td>
                    <td className="text-right p-3">{scenario1.businessHours} h</td>
                    <td className="text-right p-3">{scenario2.businessHours} h</td>
                    <td className="text-right p-3 font-semibold text-green-600">{scenario3.totalHours} h</td>
                  </tr>
                  <tr className="border-b bg-purple-50/50 dark:bg-purple-950/20">
                    <td className="p-3 font-medium">Horas Tecnicas</td>
                    <td className="text-right p-3">{scenario1.techHours.toLocaleString()} h</td>
                    <td className="text-right p-3">{scenario2.techHours} h</td>
                    <td className="text-right p-3 font-semibold text-green-600">Incluidas en PO</td>
                  </tr>
                  <tr className="border-b font-semibold">
                    <td className="p-3">TOTAL HORAS</td>
                    <td className="text-right p-3 text-red-600">{scenario1.totalHours.toLocaleString()} h</td>
                    <td className="text-right p-3 text-amber-600">{scenario2.totalHours} h</td>
                    <td className="text-right p-3 text-green-700 text-lg">{scenario3.totalHours} h</td>
                  </tr>
                  <tr className="border-b font-semibold bg-gray-50 dark:bg-gray-900">
                    <td className="p-3">COSTE TOTAL</td>
                    <td className="text-right p-3 text-red-600">{scenario1.totalCost.toLocaleString()}€</td>
                    <td className="text-right p-3 text-amber-600">{scenario2.totalCost.toLocaleString()}€</td>
                    <td className="text-right p-3 text-green-700 text-lg">{scenario3.totalCost.toLocaleString()}€</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Metricas de ahorro */}
            <div className="bg-gradient-to-r from-green-100 to-emerald-50 dark:from-green-900 dark:to-emerald-950 rounded-lg p-6">
              <h4 className="font-semibold mb-4 text-center">Vibe Coding vs Estandar Actual de TYPSA (Escenario 2)</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
                <div>
                  <p className="text-sm text-muted-foreground">Reduccion de horas</p>
                  <p className="text-3xl font-bold text-green-700 dark:text-green-300">{hoursReductionVsCopilot}%</p>
                  <p className="text-xs text-muted-foreground">{scenario2.totalHours}h → {scenario3.totalHours}h</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Ahorro economico</p>
                  <p className="text-3xl font-bold text-green-700 dark:text-green-300">{savingsVsCopilot.toLocaleString()}€</p>
                  <p className="text-xs text-muted-foreground">({costReductionVsCopilot}%)</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">ROI vs Tradicional</p>
                  <p className="text-3xl font-bold text-green-700 dark:text-green-300">{roi}%</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Time-to-market</p>
                  <p className="text-3xl font-bold text-green-700 dark:text-green-300">3+ meses</p>
                  <p className="text-xs text-green-600">de ventaja</p>
                </div>
              </div>
            </div>

            {/* Graficos de barras */}
            <div className="space-y-4">
              <h4 className="font-semibold">Comparativa Visual de Horas</h4>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-red-600">Esc. 1: Tradicional</span>
                    <span className="font-semibold">{scenario1.totalHours.toLocaleString()} horas</span>
                  </div>
                  <div className="h-8 bg-red-400 dark:bg-red-600 rounded-full w-full"></div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-amber-600">Esc. 2: Copilot (TYPSA actual)</span>
                    <span className="font-semibold">{scenario2.totalHours} horas</span>
                  </div>
                  <div className="h-8 bg-amber-400 dark:bg-amber-600 rounded-full" style={{ width: `${(scenario2.totalHours / scenario1.totalHours) * 100}%` }}></div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-green-600 font-semibold">Esc. 3: Vibe Coding</span>
                    <span className="font-semibold">{scenario3.totalHours} horas</span>
                  </div>
                  <div className="h-8 bg-green-500 rounded-full" style={{ width: `${(scenario3.totalHours / scenario1.totalHours) * 100}%` }}></div>
                </div>
              </div>
            </div>

            {/* Conclusion clave */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 rounded-lg p-6 border-l-4 border-blue-500">
              <h4 className="font-bold text-lg mb-3 text-blue-800 dark:text-blue-200">Conclusion: El verdadero salto de productividad</h4>
              <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                El <strong>Vibe Coding</strong> ataca directamente las tareas que mas tiempo consumen, <strong>fusionando el diseno, la programacion y el QA en un unico flujo de trabajo</strong> orquestado en lenguaje natural.
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                Si bien el modelo intermedio que usamos actualmente en TYPSA Digital Solutions (Escenario 2 con Copilot) ya redujo el esfuerzo de <strong>1.600 a 980 horas</strong>, dar el salto al Vibe Coding rompe definitivamente los cuellos de botella departamentales para dejarlo en <strong>solo 56 horas</strong>.
              </p>
              <div className="bg-white dark:bg-gray-900 rounded-lg p-4 mt-4">
                <p className="text-center font-semibold text-lg">
                  Esto supone un <span className="text-green-600 text-2xl">{hoursReductionVsCopilot}% de eficiencia</span> respecto a nuestro propio estandar actual
                </p>
                <p className="text-center text-sm text-muted-foreground mt-2">
                  La verdadera rentabilidad no esta en que los programadores tecleen mas rapido, <br/>
                  <strong>sino en orquestar el producto completo desde negocio.</strong>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Conclusión */}
        <Card>
          <CardHeader>
            <CardTitle>Conclusion Estrategica</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>
              La inversion en el desarrollo del CRM TYPSA Digital Solutions mediante tecnologia de IA generativa 
              ha demostrado ser <strong>altamente rentable</strong>, con un ahorro del {costReductionVsTraditional}% respecto 
              al desarrollo tradicional y un time-to-market reducido de 4 meses a 3 semanas.
            </p>
            <div className="bg-muted p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Resumen de Inversion - Tres Escenarios</h4>
              <ul className="text-sm space-y-1">
                <li>• <strong>Escenario 1 (Tradicional):</strong> {scenario1.totalHours.toLocaleString()}h → {scenario1.totalCost.toLocaleString()}€</li>
                <li>• <strong>Escenario 2 (Copilot/TYPSA actual):</strong> {scenario2.totalHours}h → {scenario2.totalCost.toLocaleString()}€</li>
                <li>• <strong>Escenario 3 (Vibe Coding):</strong> {scenario3.totalHours}h → {scenario3.totalCost.toLocaleString()}€</li>
                <li>• <strong>Ahorro vs Tradicional:</strong> {savingsVsTraditional.toLocaleString()}€ ({costReductionVsTraditional}%)</li>
                <li>• <strong>Ahorro vs TYPSA actual:</strong> {savingsVsCopilot.toLocaleString()}€ ({costReductionVsCopilot}%)</li>
                <li>• <strong>ROI:</strong> {roi}%</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground py-4 border-t">
          <p className="font-semibold">Informe Técnico CRM TYPSA Digital Solutions</p>
          <p>Generado el {formattedDate}</p>
          <p className="text-xs mt-1">Desarrollado con Emergent AI Platform</p>
        </div>
      </div>
    </div>
  );
};

export default TechnicalReport;
